import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Divider,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Visibility,
  VisibilityOff,
  Send as SendIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LanguageSelector from '../components/LanguageSelector';
import myhomeLogo from '../assests/myhome-logo.png';

function validatePassword(password) {
  const minLength = 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  return (
    password.length >= minLength && hasUpper && hasLower && hasNumber && hasSpecial
  );
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validatePhoneNumber(phone) {
  // Support Indian phone numbers: +91XXXXXXXXXX or XXXXXXXXXX
  const phoneRegex = /^(\+91)?[6-9]\d{9}$/;
  return phoneRegex.test(phone.replace(/\s+/g, ''));
}

function formatPhoneNumber(phone) {
  // Ensure phone number has +91 prefix for Cognito
  const cleaned = phone.replace(/\s+/g, '').replace(/^\+91/, '');
  if (cleaned.length === 10 && /^[6-9]\d{9}$/.test(cleaned)) {
    return `+91${cleaned}`;
  }
  return phone;
}

const Login = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Authentication method state
  const [authMethod, setAuthMethod] = useState(0); // 0: Email, 1: Phone
  
  // Email authentication states
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  
  // MSG91 specific states
  const [showPasswordChallenge, setShowPasswordChallenge] = useState(false);
  const [challengePassword, setChallengePassword] = useState('');
  const [challengeSession, setChallengeSession] = useState('');
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  
  // Common states
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  
  // Forgot Password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetCodeError, setResetCodeError] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetNewPasswordError, setResetNewPasswordError] = useState('');
  const [resetTimer, setResetTimer] = useState(0);
  const [resetLoading, setResetLoading] = useState(false);
  const [additionalAttributes, setAdditionalAttributes] = useState({});
  const [additionalAttrErrors, setAdditionalAttrErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { 
    login, 
    user, 
    newPasswordChallenge, 
    completeNewPassword,
    otpChallenge,
    initiatePhoneAuth,
    verifyOTP,
    resendOTP,
    clearOtpChallenge,
    // Forgot Password functions
    forgotPassword,
    confirmPasswordReset,
    // MSG91 functions
    sendMSG91OTP,
    verifyMSG91OTP,
    completeMSG91Password,
    registerMSG91User,
    resendMSG91OTP,
    msg91Challenge,
    registrationData
  } = useAuth();
  const navigate = useNavigate();

  // Compute missing required attributes for Cognito new password flow
  const alwaysRequired = ['given_name', 'family_name', 'email', 'phone_number'];
  let missingAttributes = [];
  if (newPasswordChallenge) {
    const attrs = newPasswordChallenge.userAttributes || {};
    alwaysRequired.forEach(attr => {
      if (!attrs[attr]) {
        missingAttributes.push(attr);
      }
    });
    (newPasswordChallenge.userAttributes.requiredAttributes || []).forEach(attr => {
      if (!attrs[attr] && !missingAttributes.includes(attr)) {
        missingAttributes.push(attr);
      }
    });
  }

  // Redirect if user is already authenticated
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Reset timer countdown for forgot password
  useEffect(() => {
    let interval = null;
    if (resetTimer > 0) {
      interval = setInterval(() => {
        setResetTimer(timer => timer - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resetTimer]);

  // OTP timer countdown
  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(timer => timer - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpTimer]);

  // Handle tab change
  const handleAuthMethodChange = (event, newValue) => {
    setAuthMethod(newValue);
    setError('');
    setEmailError('');
    setPhoneError('');
    setOtpError('');
    setOtpSent(false);
    setOtpCode('');
    // Reset forgot password state
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordError('');
    setResetCodeSent(false);
    setResetCode('');
    setResetCodeError('');
    setResetNewPassword('');
    setResetNewPasswordError('');
    setResetTimer(0);
    clearOtpChallenge();
  };

  // Email/Password authentication
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailError('');
    setLoading(true);
    
    if (!validateEmail(email)) {
      setEmailError(t('auth.invalidEmail'));
      setLoading(false);
      return;
    }
    
    try {
      const result = await login(email, password);
      if (!result.success && result.error === 'NEW_PASSWORD_REQUIRED') {
        setError('');
      } else if (!result.success) {
        setError(result.error || t('auth.loginFailed'));
      }
    } catch (err) {
      setError(t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password - Send reset code
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordError('');
    setResetLoading(true);

    if (!validateEmail(forgotPasswordEmail)) {
      setForgotPasswordError(t('auth.invalidEmail'));
      setResetLoading(false);
      return;
    }

    try {
      const result = await forgotPassword(forgotPasswordEmail);
      if (result.success) {
        setResetCodeSent(true);
        setResetTimer(300); // 5 minutes
        setForgotPasswordError('');
      } else {
        setForgotPasswordError(result.error || 'Failed to send reset code');
      }
    } catch (err) {
      setForgotPasswordError('Error sending reset code. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  // Confirm Password Reset
  const handlePasswordResetConfirm = async (e) => {
    e.preventDefault();
    setResetCodeError('');
    setResetNewPasswordError('');
    setResetLoading(true);

    if (!resetCode || resetCode.length !== 6) {
      setResetCodeError('Please enter a valid 6-digit code');
      setResetLoading(false);
      return;
    }

    if (!validatePassword(resetNewPassword)) {
      setResetNewPasswordError('Password must be at least 8 characters with uppercase, lowercase, number and special character');
      setResetLoading(false);
      return;
    }

    try {
      const result = await confirmPasswordReset(
        forgotPasswordEmail,
        resetCode,
        resetNewPassword
      );
      
      if (result.success) {
        // Reset successful - go back to login
        setShowForgotPassword(false);
        setResetCodeSent(false);
        setForgotPasswordEmail('');
        setResetCode('');
        setResetNewPassword('');
        setError('');
        setEmail(forgotPasswordEmail);
        // Show success message
        alert('Password reset successful! Please login with your new password.');
      } else {
        setResetCodeError(result.error || 'Failed to reset password');
      }
    } catch (err) {
      setResetCodeError('Error resetting password. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  // Resend reset code
  const handleResendResetCode = async () => {
    if (resetTimer > 0) return;
    
    setResetLoading(true);
    try {
      const result = await forgotPassword(forgotPasswordEmail);
      if (result.success) {
        setResetTimer(300); // 5 minutes
        setResetCodeError('');
      } else {
        setResetCodeError(result.error || 'Failed to resend reset code');
      }
    } catch (err) {
      setResetCodeError('Error resending reset code. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  // Phone/OTP authentication - Step 1: Send OTP using MSG91
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPhoneError('');
    setLoading(true);
    
    if (!validatePhoneNumber(phoneNumber)) {
      setPhoneError(t('auth.invalidMobile'));
      setLoading(false);
      return;
    }
    
    try {
      const result = await sendMSG91OTP(phoneNumber);
      
      if (result.success) {
        setOtpSent(true);
        setOtpTimer(60); // 60 seconds countdown
        setError('');
        setAttemptsRemaining(3); // Reset attempts
      } else {
        setError(result.error || t('auth.otpSendFailed'));
      }
    } catch (err) {
      setError(t('auth.otpSendError'));
    } finally {
      setLoading(false);
    }
  };

  // Phone/OTP authentication - Step 2: Verify OTP using MSG91
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setOtpError('');
    setLoading(true);
    
    if (!otpCode || otpCode.length !== 6) {
      setOtpError(t('auth.invalidOTP'));
      setLoading(false);
      return;
    }
    
    try {
      const result = await verifyMSG91OTP(phoneNumber, otpCode);
      
      if (result.success) {
        if (result.authenticated) {
          // User logged in successfully
          setError('');
        } else if (result.requiresPassword) {
          // User needs to set password
          setShowPasswordChallenge(true);
          setChallengeSession(result.session);
          setOtpSent(false);
        } else if (result.requiresRegistration) {
          // New user needs to register
          setShowRegistration(true);
          setOtpSent(false);
        }
      } else {
        setError(result.error || t('auth.otpVerifyFailed'));
        if (result.attemptsRemaining !== undefined) {
          setAttemptsRemaining(result.attemptsRemaining);
        }
      }
    } catch (err) {
      setError(t('auth.otpVerifyError'));
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP using MSG91
  const handleResendOTP = async () => {
    if (otpTimer > 0) return;
    
    setLoading(true);
    setError('');
    
    try {
      const result = await resendMSG91OTP(phoneNumber);
      
      if (result.success) {
        setOtpTimer(60);
        setError('');
        setAttemptsRemaining(3); // Reset attempts
      } else {
        setError(result.error || t('auth.otpResendFailed'));
      }
    } catch (err) {
      setError(t('auth.otpResendError'));
    } finally {
      setLoading(false);
    }
  };

  // Handle password challenge completion
  const handlePasswordChallengeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validatePassword(challengePassword)) {
      setError(t('auth.passwordRequirements'));
      setLoading(false);
      return;
    }

    try {
      const result = await completeMSG91Password(phoneNumber, challengePassword, challengeSession);
      
      if (result.success) {
        setShowPasswordChallenge(false);
        setChallengePassword('');
        setChallengeSession('');
        setError('');
      } else {
        setError(result.error || 'Failed to set password');
      }
    } catch (err) {
      setError('Error setting password');
    } finally {
      setLoading(false);
    }
  };

  // Handle user registration
  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { firstName, lastName, email, password } = registrationForm;

    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      setLoading(false);
      return;
    }

    if (email && !validateEmail(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (password && !validatePassword(password)) {
      setError(t('auth.passwordRequirements'));
      setLoading(false);
      return;
    }

    try {
      const result = await registerMSG91User(
        phoneNumber, 
        firstName, 
        lastName, 
        email || null, 
        password || null
      );
      
      if (result.success) {
        setShowRegistration(false);
        setRegistrationForm({ firstName: '', lastName: '', email: '', password: '' });
        setError('');
        // After registration, send OTP again for login
        handlePhoneSubmit({ preventDefault: () => {} });
      } else {
        setError(result.error || 'Registration failed');
      }
    } catch (err) {
      setError('Registration error');
    } finally {
      setLoading(false);
    }
  };

  // Handle registration form changes
  const handleRegistrationChange = (e) => {
    const { name, value } = e.target;
    setRegistrationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    if (!validatePassword(value)) {
      setNewPasswordError(
        t('auth.passwordRequirements')
      );
    } else {
      setNewPasswordError('');
    }
  };

  const handleAttributeChange = (e) => {
    const { name, value } = e.target;
    setAdditionalAttributes({
      ...additionalAttributes,
      [name]: value,
    });
    if (name === 'email') {
      setAdditionalAttrErrors({
        ...additionalAttrErrors,
        [name]: validateEmail(value) ? '' : 'Please enter a valid email address.'
      });
    } else {
      setAdditionalAttrErrors({ ...additionalAttrErrors, [name]: '' });
    }
  };

  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNewPasswordError('');
    setLoading(true);
    let hasError = false;
    let attrErrors = {};
    missingAttributes.forEach(attr => {
      if (attr === 'email') {
        if (!validateEmail(additionalAttributes[attr] || '')) {
          attrErrors[attr] = 'Please enter a valid email address.';
          hasError = true;
        }
      } else if (!additionalAttributes[attr]) {
        attrErrors[attr] = 'This field is required.';
        hasError = true;
      }
    });
    setAdditionalAttrErrors(attrErrors);
    if (hasError) {
      setLoading(false);
      return;
    }
    if (!validatePassword(newPassword)) {
      setNewPasswordError(
        'Password must be at least 8 characters and include upper, lower, number, and special character.'
      );
      setLoading(false);
      return;
    }
    try {
      const attributes = { ...additionalAttributes };
      const result = await completeNewPassword(newPassword, attributes);
      if (!result.success) {
        setError(result.error || t('auth.newPasswordFailed'));
      }
    } catch (err) {
      setError(t('auth.newPasswordError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: theme.palette.background.default,
      px: 2,
      boxSizing: 'border-box',
    }}>
      <Paper
        elevation={4}
        sx={{
          width: 'auto',
          maxWidth: 340,
          mx: 'auto',
          my: 'auto',
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        {/* Language Selector */}
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <LanguageSelector variant="standard" showLabel={false} sx={{ minWidth: 100 }} />
        </Box>

        <Box sx={{ mb: 2, mt: 1 }}>
          <img
            src={myhomeLogo}
            alt="MyHome Logo"
            style={{
              height: isMobile ? '32px' : '40px',
              width: 'auto',
              display: 'block',
              margin: '0 auto'
            }}
          />
        </Box>
        <Typography component="h2" variant="h5" color="textSecondary" gutterBottom sx={{ mt: 1, fontSize: { xs: '20px', sm: '24px' }, textAlign: 'center' }}>
          {t('auth.signIn')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
            {error}
          </Alert>
        )}

          {/* New Password Required Flow */}
          {newPasswordChallenge ? (
            <Box component="form" onSubmit={handleNewPasswordSubmit} sx={{ mt: 1, width: '100%' }}>
              <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                {t('auth.newPasswordRequired')}
              </Alert>
              <TextField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label={t('auth.newPassword')}
                type="password"
                id="newPassword"
                autoComplete="new-password"
                value={newPassword}
                onChange={handleNewPasswordChange}
                disabled={loading}
                error={!!newPasswordError}
                helperText={newPasswordError}
                sx={{ fontSize: { xs: '16px', sm: '18px' } }}
              />
              {missingAttributes.map((attr) => (
                <TextField
                  key={attr}
                  margin="normal"
                  required
                  fullWidth
                  name={attr}
                  label={attr.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  value={additionalAttributes[attr] || ''}
                  onChange={handleAttributeChange}
                  disabled={loading}
                  error={!!additionalAttrErrors[attr]}
                  helperText={additionalAttrErrors[attr]}
                  sx={{ fontSize: { xs: '16px', sm: '18px' } }}
                />
              ))}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, minHeight: 48, fontSize: { xs: '16px', sm: '18px' } }}
                disabled={loading || !!newPasswordError || Object.values(additionalAttrErrors).some(Boolean)}
              >
                {loading ? <CircularProgress size={24} /> : t('auth.setNewPassword')}
              </Button>
            </Box>
          ) : (
            /* Main Login Interface */
            <Box sx={{ width: '100%', mt: 2 }}>
              {/* Authentication Method Tabs */}
              <Tabs
                value={authMethod}
                onChange={handleAuthMethodChange}
                variant="fullWidth"
                sx={{ 
                  mb: 3,
                  '& .MuiTab-root': {
                    minHeight: 48,
                    fontSize: { xs: '14px', sm: '16px' }
                  }
                }}
              >
                <Tab 
                  icon={<EmailIcon />} 
                  label={t('auth.email')} 
                  iconPosition="start"
                />
                <Tab 
                  icon={<PhoneIcon />} 
                  label={t('auth.mobile')} 
                  iconPosition="start"
                />
              </Tabs>

              {/* Email/Password Authentication */}
              {authMethod === 0 && (
                <Box sx={{ width: '100%' }}>
                  {/* Forgot Password Flow */}
                  {showForgotPassword ? (
                    <Box sx={{ width: '100%' }}>
                      {!resetCodeSent ? (
                        /* Send Reset Code Form */
                        <Box component="form" onSubmit={handleForgotPasswordSubmit} sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                            Reset Password
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: 'center' }}>
                            Enter your email address and we'll send you a reset code
                          </Typography>
                          {forgotPasswordError && (
                            <Alert severity="error" sx={{ width: '90%', mb: 2 }}>
                              {forgotPasswordError}
                            </Alert>
                          )}
                          <TextField
                            margin="normal"
                            required
                            id="forgotPasswordEmail"
                            label={t('auth.emailAddress')}
                            name="forgotPasswordEmail"
                            autoComplete="email"
                            autoFocus
                            value={forgotPasswordEmail}
                            onChange={(e) => setForgotPasswordEmail(e.target.value)}
                            disabled={resetLoading}
                            error={!!forgotPasswordError}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ fontSize: { xs: '16px', sm: '18px' }, width: '90%' }}
                          />
                          <Button
                            type="submit"
                            variant="contained"
                            sx={{ mt: 3, mb: 2, minHeight: 40, fontSize: { xs: '15px', sm: '16px' }, width: '90%' }}
                            disabled={resetLoading}
                          >
                            {resetLoading ? <CircularProgress size={24} /> : 'Send Reset Code'}
                          </Button>
                          <Button
                            variant="text"
                            onClick={() => {
                              setShowForgotPassword(false);
                              setForgotPasswordEmail('');
                              setForgotPasswordError('');
                            }}
                            sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                          >
                            Back to Login
                          </Button>
                        </Box>
                      ) : (
                        /* Verify Reset Code and Set New Password Form */
                        <Box component="form" onSubmit={handlePasswordResetConfirm} sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                            Enter Reset Code
                          </Typography>
                          <Alert severity="info" sx={{ width: '90%', mb: 2 }}>
                            We've sent a reset code to {forgotPasswordEmail}
                            {resetTimer > 0 && (
                              <Typography variant="caption" display="block">
                                Code expires in: {Math.floor(resetTimer / 60)}:{(resetTimer % 60).toString().padStart(2, '0')}
                              </Typography>
                            )}
                          </Alert>
                          {resetCodeError && (
                            <Alert severity="error" sx={{ width: '90%', mb: 2 }}>
                              {resetCodeError}
                            </Alert>
                          )}
                          <TextField
                            margin="normal"
                            required
                            id="resetCode"
                            label="Reset Code"
                            name="resetCode"
                            autoFocus
                            value={resetCode}
                            onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            disabled={resetLoading}
                            error={!!resetCodeError}
                            inputProps={{ 
                              maxLength: 6,
                              style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.3rem' }
                            }}
                            sx={{ fontSize: { xs: '16px', sm: '18px' }, width: '90%' }}
                          />
                          <TextField
                            margin="normal"
                            required
                            name="resetNewPassword"
                            label="New Password"
                            type="password"
                            value={resetNewPassword}
                            onChange={(e) => setResetNewPassword(e.target.value)}
                            disabled={resetLoading}
                            error={!!resetNewPasswordError}
                            helperText={resetNewPasswordError || 'Password must be at least 8 characters with uppercase, lowercase, number and special character'}
                            sx={{ fontSize: { xs: '16px', sm: '18px' }, width: '90%' }}
                          />
                          <Button
                            type="submit"
                            variant="contained"
                            sx={{ mt: 3, mb: 2, minHeight: 40, fontSize: { xs: '15px', sm: '16px' }, width: '90%' }}
                            disabled={resetLoading || resetCode.length !== 6 || !validatePassword(resetNewPassword)}
                          >
                            {resetLoading ? <CircularProgress size={24} /> : 'Reset Password'}
                          </Button>
                          <Stack direction="row" spacing={2} sx={{ width: '90%' }}>
                            <Button
                              variant="outlined"
                              onClick={handleResendResetCode}
                              disabled={resetLoading || resetTimer > 0}
                              startIcon={<RefreshIcon />}
                              sx={{ minHeight: 40, fontSize: { xs: '14px', sm: '16px' } }}
                            >
                              {resetTimer > 0 ? `Resend in ${Math.ceil(resetTimer / 60)}m` : 'Resend Code'}
                            </Button>
                            <Button
                              variant="text"
                              onClick={() => {
                                setShowForgotPassword(false);
                                setResetCodeSent(false);
                                setForgotPasswordEmail('');
                                setResetCode('');
                                setResetNewPassword('');
                                setResetCodeError('');
                                setResetNewPasswordError('');
                                setResetTimer(0);
                              }}
                              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    /* Normal Login Form */
                    <Box component="form" onSubmit={handleEmailSubmit} sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <TextField
                        margin="normal"
                        required
                        id="email"
                        label={t('auth.emailAddress')}
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                        error={!!emailError}
                        helperText={emailError}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ fontSize: { xs: '16px', sm: '18px' }, width: '90%' }}
                      />
                      <TextField
                        margin="normal"
                        required
                        name="password"
                        label={t('auth.password')}
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ fontSize: { xs: '16px', sm: '18px' }, width: '90%' }}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        sx={{ mt: 3, mb: 2, minHeight: 40, fontSize: { xs: '15px', sm: '16px' }, width: '90%' }}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={24} /> : t('auth.signInWithEmail')}
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => {
                          setShowForgotPassword(true);
                          setForgotPasswordEmail(email);
                        }}
                        sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                      >
                        Forgot Password?
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* Mobile/OTP Authentication with MSG91 */}
              {authMethod === 1 && (
                <Box sx={{ width: '100%' }}>
                  {/* Password Challenge Form */}
                  {showPasswordChallenge ? (
                    <Box component="form" onSubmit={handlePasswordChallengeSubmit}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Please set a password for your account to complete login.
                      </Alert>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="challengePassword"
                        label="Set New Password"
                        type="password"
                        value={challengePassword}
                        onChange={(e) => setChallengePassword(e.target.value)}
                        disabled={loading}
                        helperText="Password must be at least 8 characters with uppercase, lowercase, number and special character"
                        sx={{ fontSize: { xs: '16px', sm: '18px' } }}
                      />
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2, minHeight: 48, fontSize: { xs: '16px', sm: '18px' } }}
                        disabled={loading || !validatePassword(challengePassword)}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Set Password & Login'}
                      </Button>
                    </Box>
                  ) : 
                  /* Registration Form */
                  showRegistration ? (
                    <Box component="form" onSubmit={handleRegistrationSubmit}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Welcome! Please complete your registration for {phoneNumber}.
                      </Alert>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="firstName"
                        label="First Name"
                        value={registrationForm.firstName}
                        onChange={handleRegistrationChange}
                        disabled={loading}
                        sx={{ fontSize: { xs: '16px', sm: '18px' } }}
                      />
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="lastName"
                        label="Last Name"
                        value={registrationForm.lastName}
                        onChange={handleRegistrationChange}
                        disabled={loading}
                        sx={{ fontSize: { xs: '16px', sm: '18px' } }}
                      />
                      <TextField
                        margin="normal"
                        fullWidth
                        name="email"
                        label="Email (Optional)"
                        type="email"
                        value={registrationForm.email}
                        onChange={handleRegistrationChange}
                        disabled={loading}
                        sx={{ fontSize: { xs: '16px', sm: '18px' } }}
                      />
                      <TextField
                        margin="normal"
                        fullWidth
                        name="password"
                        label="Password (Optional - will be generated if not provided)"
                        type="password"
                        value={registrationForm.password}
                        onChange={handleRegistrationChange}
                        disabled={loading}
                        helperText="Leave empty to auto-generate a temporary password"
                        sx={{ fontSize: { xs: '16px', sm: '18px' } }}
                      />
                      <Stack direction="row" spacing={2} sx={{ mt: 3, mb: 2 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          sx={{ flex: 1, minHeight: 48, fontSize: { xs: '16px', sm: '18px' } }}
                          disabled={loading || !registrationForm.firstName.trim() || !registrationForm.lastName.trim()}
                        >
                          {loading ? <CircularProgress size={24} /> : 'Register'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setShowRegistration(false);
                            setOtpSent(false);
                          }}
                          sx={{ minHeight: 48, fontSize: { xs: '14px', sm: '16px' } }}
                        >
                          Back
                        </Button>
                      </Stack>
                    </Box>
                  ) :
                  !otpSent ? (
                    /* Phone Number Entry */
                    <Box component="form" onSubmit={handlePhoneSubmit}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="phoneNumber"
                        label={t('auth.mobileNumber')}
                        name="phoneNumber"
                        placeholder={t('auth.mobileNumberPlaceholder')}
                        autoFocus
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        disabled={loading}
                        error={!!phoneError}
                        helperText={phoneError || t('auth.mobileFormat')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ fontSize: { xs: '16px', sm: '18px' } }}
                      />
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2, minHeight: 48, fontSize: { xs: '16px', sm: '18px' } }}
                        disabled={loading}
                        startIcon={<SendIcon />}
                      >
                        {loading ? <CircularProgress size={24} /> : t('auth.sendOTP')}
                      </Button>
                    </Box>
                  ) : (
                    /* OTP Verification */
                    <Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {t('auth.otpSentTo', { phoneNumber: formatPhoneNumber(phoneNumber) })}
                        {attemptsRemaining !== null && (
                          <br />
                        )}
                        {attemptsRemaining !== null && attemptsRemaining < 3 && (
                          <Typography variant="caption" color="warning.main">
                            Attempts remaining: {attemptsRemaining}
                          </Typography>
                        )}
                      </Alert>
                      
                      <Box component="form" onSubmit={handleOtpSubmit}>
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          id="otpCode"
                          label={t('auth.enterOTP')}
                          name="otpCode"
                          placeholder={t('auth.otpPlaceholder')}
                          autoFocus
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          disabled={loading}
                          error={!!otpError}
                          helperText={otpError}
                          inputProps={{ 
                            maxLength: 6,
                            style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                          }}
                          sx={{ fontSize: { xs: '16px', sm: '18px' } }}
                        />
                        <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 2 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            sx={{ flex: 1, minHeight: 48, fontSize: { xs: '16px', sm: '18px' } }}
                            disabled={loading || otpCode.length !== 6}
                          >
                            {loading ? <CircularProgress size={24} /> : t('auth.verifyOTP')}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={handleResendOTP}
                            disabled={loading || otpTimer > 0}
                            startIcon={<RefreshIcon />}
                            sx={{ minHeight: 48, fontSize: { xs: '14px', sm: '16px' } }}
                          >
                            {otpTimer > 0 ? `${otpTimer}s` : t('auth.resend')}
                          </Button>
                        </Stack>
                        <Button
                          variant="text"
                          onClick={() => {
                            setOtpSent(false);
                            setOtpCode('');
                            setOtpTimer(0);
                            setAttemptsRemaining(null);
                            setShowPasswordChallenge(false);
                            setShowRegistration(false);
                          }}
                          sx={{ width: '100%' }}
                        >
                          Change Phone Number
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Don't have an account? Contact your <a href="mailto:contact@innoture.co.in">administrator</a>.
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
    </Box>
  );
};

export default Login;